/**
 * WorkerEvaluationReport — performance leaderboard per worker.
 *
 * Aggregates attendance + time-tracking over a date range and ranks workers
 * by a composite score (attendance + realization + on-time).
 * Click a row to expand the daily breakdown.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Stack, Avatar, Chip, Button, TextField, MenuItem, LinearProgress, CircularProgress,
  IconButton, Collapse, Tooltip, Menu, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import DownloadIcon          from '@mui/icons-material/Download';
import SearchIcon            from '@mui/icons-material/Search';
import InputAdornment        from '@mui/material/InputAdornment';
import ClearIcon             from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EmojiEventsIcon       from '@mui/icons-material/EmojiEvents';
import RefreshIcon           from '@mui/icons-material/Refresh';
import LeaderboardIcon       from '@mui/icons-material/Leaderboard';
import GroupsIcon            from '@mui/icons-material/Groups';
import AccessTimeIcon        from '@mui/icons-material/AccessTime';
import SpeedIcon             from '@mui/icons-material/Speed';
import * as XLSX             from 'xlsx';
import jsPDF                 from 'jspdf';
import autoTable             from 'jspdf-autotable';
import presenceService       from '../../services/presenceService';
import apiService, { authFetch } from '../../services/apiServices';
import { activeWorkers, wasEmployedOn } from '../../utils/workerStatus';

// ─── helpers ─────────────────────────────────────────────────────────────────
// Dita LOKALE: `toISOString()` është UTC dhe para orës 02:00 jepte ditën e djeshme.
const localYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayStr   = () => localYMD(new Date());
// Default range: last 30 days — month-to-date is nearly empty at the start of a month.
const rangeStart = () => { const d = new Date(); d.setDate(d.getDate() - 30); return localYMD(d); };

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toTimeString().slice(0, 5);
};

const fmtHHMM = (minutes) => {
  const m = Math.max(0, Math.round(minutes));
  return `${Math.floor(m / 60)}:${('0' + (m % 60)).slice(-2)}`;
};

const initials = (name) =>
  (name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);

const computeBreakMin = (breaks) =>
  (breaks || []).reduce((s, b) => {
    const st = b.start ? new Date(b.start).getTime() : NaN;
    const en = b.end   ? new Date(b.end).getTime()   : NaN;
    return s + (!isNaN(st) && !isNaN(en) && en > st ? Math.round((en - st) / 60000) : 0);
  }, 0);

const computePresMin = (s) => {
  if (!s.checkIn) return 0;
  const start = new Date(s.checkIn).getTime();
  const end   = s.checkOut ? new Date(s.checkOut).getTime() : start;
  return Math.max(0, Math.round((end - start) / 60000) - computeBreakMin(s.breaks));
};

const eventsToSessions = (events) => {
  const groups = new Map();
  (events || []).forEach(e => {
    const at  = new Date(e.At || e.at);
    if (isNaN(at)) return;
    const day = localYMD(at);
    const id  = String(e.WorkerId || e.workerId || '');
    if (!id) return;
    const key = day + '__' + id;
    if (!groups.has(key)) groups.set(key, { day, workerId: id, checkIn: null, checkOut: null, breaks: [] });
    const g = groups.get(key);
    switch ((e.Type || e.type || '').toLowerCase().replace(/-/g, '_')) {
      case 'checkin':
      case 'check_in':
      case 'in':
        if (!g.checkIn || at < new Date(g.checkIn)) g.checkIn = at.toISOString();
        break;
      case 'checkout':
      case 'check_out':
      case 'out':
        g.checkOut = at.toISOString();
        break;
      case 'break_start':
        g.breaks.push({ start: at.toISOString() });
        break;
      case 'break_end':
        for (let i = g.breaks.length - 1; i >= 0; i--) {
          if (!g.breaks[i].end) { g.breaks[i].end = at.toISOString(); break; }
        }
        break;
      default: break;
    }
  });
  return Array.from(groups.values());
};

// Rikthim i vjetër: 8 orë të koduara. Pikët e prezencës vijnë nga serveri
// (`/api/reports/attendance-rate`, orari i vërtetë i secilit); ky numër mbetet
// vetëm për `realizimPct` të time-tracking-ut dhe kur endpoint-i s'përgjigjet.
const STANDARD_MIN  = 8 * 60;     // 8h work day
const ON_TIME_HOUR  = 9;          // worker is "on time" if checkIn <= 09:00

const scoreBand = (score) => {
  if (score >= 90) return { label: 'Shkëlqyeshëm',   color: 'success' };
  if (score >= 75) return { label: 'Shumë mirë',     color: 'info'    };
  if (score >= 50) return { label: 'Mesatar',        color: 'warning' };
  return              { label: 'Për përmirësim', color: 'error'   };
};

// ─── Component ───────────────────────────────────────────────────────────────
export default function WorkerEvaluationReport() {
  const { t } = useTranslation();
  const [dateFrom,    setDateFrom]    = useState(rangeStart());
  const [dateTo,      setDateTo]      = useState(todayStr());
  const [fWorker,     setFWorker]     = useState('');
  const [fDept,       setFDept]       = useState('');
  const [fSearch,     setFSearch]     = useState('');
  const [loading,     setLoading]     = useState(false);
  const [events,      setEvents]      = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [workers,     setWorkers]     = useState([]);
  const [departments, setDepartments] = useState([]);
  const [expanded,    setExpanded]    = useState(new Set());
  const [exportAnchor, setExportAnchor] = useState(null);
  // worker_id → attendance_rate nga serveri për intervalin e zgjedhur.
  const [rateMap,     setRateMap]     = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, dRes, ttRes, evts] = await Promise.all([
        apiService().get('workers'),
        apiService().get('departments'),
        apiService().get('timeEntries').catch(() => ({ success: false, data: [] })),
        presenceService().getRange(dateFrom, dateTo),
      ]);
      if (wRes.success)  setWorkers(wRes.data || []);
      if (dRes.success)  setDepartments(dRes.data || []);
      if (ttRes.success) setTimeEntries(ttRes.data || []);
      setEvents(evts || []);
      try {
        const r = await authFetch(`/api/reports/attendance-rate?period=custom&from=${encodeURIComponent(dateFrom)}&to=${encodeURIComponent(dateTo)}`);
        const j = await r.json().catch(() => ({}));
        const m = {};
        if (r.ok && j?.success) for (const e of (j.data?.employees || [])) if (e.attendance_rate != null) m[String(e.worker_id)] = e.attendance_rate;
        setRateMap(m);
      } catch { setRateMap({}); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const sessions = useMemo(() => eventsToSessions(events), [events]);

  const ttMap = useMemo(() => {
    const m = {};
    timeEntries.forEach(e => {
      if (!e.date || !e.worker_id) return;
      const k = String(e.worker_id) + '__' + e.date;
      m[k] = (m[k] || 0) + (e.duration_seconds || 0);
    });
    return m;
  }, [timeEntries]);

  const workerById = useCallback(id => workers.find(w => String(w.Id || w.id) === String(id)), [workers]);
  const wName      = useCallback(id => { const w = workerById(id); return w ? `${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.trim() : ''; }, [workerById]);
  const wDeptId    = useCallback(id => { const w = workerById(id); return w ? String(w.Department || w.DepartmentId || w.department_id || '') : ''; }, [workerById]);
  const wPosition  = useCallback(id => { const w = workerById(id); return w ? (w.Position || w.position || '') : ''; }, [workerById]);
  const wAvatar    = useCallback(id => { const w = workerById(id); return w ? (w.Image || w.image || '') : ''; }, [workerById]);

  const deptName = useCallback(deptId => {
    const d = departments.find(x => String(x.Id || x.id) === String(deptId));
    return d ? (d.Name || d.name) : '';
  }, [departments]);

  // Zgjedhje, jo të dhëna — shih WorkHoursReport për të njëjtën ndarje.
  const deptWorkers = useMemo(() =>
    activeWorkers(fDept ? workers.filter(w => String(w.Department || w.DepartmentId || w.department_id || '') === String(fDept)) : workers),
  [workers, fDept]);

  // Daily rows (filtered)
  const dailyRows = useMemo(() => {
    let s = sessions.filter(r => r.day >= dateFrom && r.day <= dateTo && wasEmployedOn(workerById(r.workerId ?? r.worker_id), r.day));
    if (fWorker) s = s.filter(r => String(r.workerId) === String(fWorker));
    if (fDept)   s = s.filter(r => wDeptId(r.workerId) === String(fDept));
    if (fSearch) {
      const q = fSearch.toLowerCase().trim();
      s = s.filter(r => {
        const name = wName(r.workerId).toLowerCase();
        const pos  = wPosition(r.workerId).toLowerCase();
        const dept = deptName(wDeptId(r.workerId)).toLowerCase();
        return name.includes(q) || pos.includes(q) || dept.includes(q);
      });
    }

    return s.map(r => {
      const ttSec    = ttMap[r.workerId + '__' + r.day] || 0;
      const ttMin    = ttSec / 60;
      const presMin  = computePresMin(r);
      const breakMin = computeBreakMin(r.breaks);
      const realizimPct = Math.round((ttMin / STANDARD_MIN) * 100);
      const prezPct     = Math.round((presMin / STANDARD_MIN) * 100);
      // on time = checkIn before ON_TIME_HOUR:00 local time
      const inH = r.checkIn ? new Date(r.checkIn).getHours() + new Date(r.checkIn).getMinutes() / 60 : 99;
      const onTime = r.checkIn ? inH <= ON_TIME_HOUR : false;
      return { ...r, name: wName(r.workerId), presMin, breakMin, ttMin, ttSec, realizimPct, prezPct, onTime };
    });
  }, [sessions, fWorker, fDept, fSearch, dateFrom, dateTo, ttMap, wName, wDeptId, wPosition, deptName]);

  // Aggregate per worker
  const workerRows = useMemo(() => {
    const map = new Map();
    dailyRows.forEach(d => {
      const k = String(d.workerId);
      if (!map.has(k)) map.set(k, {
        workerId: k, name: d.name,
        days: 0, presMin: 0, ttMin: 0, breakMin: 0, onTimeDays: 0,
        firstDay: d.day, lastDay: d.day,
        items: [],
      });
      const a = map.get(k);
      a.days     += 1;
      a.presMin  += d.presMin;
      a.ttMin    += d.ttMin;
      a.breakMin += d.breakMin;
      if (d.onTime) a.onTimeDays += 1;
      if (d.day < a.firstDay) a.firstDay = d.day;
      if (d.day > a.lastDay)  a.lastDay  = d.day;
      a.items.push(d);
    });
    return Array.from(map.values()).map(a => {
      const avgPres = a.days > 0 ? a.presMin / a.days : 0;
      const avgTT   = a.days > 0 ? a.ttMin   / a.days : 0;
      // Norma e serverit kur ekziston (orari i vërtetë, leja, festat); ndryshe rikthimi 8h.
      const presScore  = rateMap[a.workerId] != null ? Math.min(100, Math.round(rateMap[a.workerId])) : Math.min(100, Math.round((avgPres / STANDARD_MIN) * 100));
      const realScore  = Math.min(100, Math.round((avgTT   / STANDARD_MIN) * 100));
      const onTimePct  = a.days > 0 ? Math.round((a.onTimeDays / a.days) * 100) : 0;
      // Composite: 40% attendance + 40% realization + 20% on-time
      const score = Math.round(presScore * 0.4 + realScore * 0.4 + onTimePct * 0.2);
      a.items.sort((x, y) => x.day.localeCompare(y.day));
      return { ...a, avgPres, avgTT, presScore, realScore, onTimePct, score };
    }).sort((a, b) => b.score - a.score || b.days - a.days || a.name.localeCompare(b.name));
  }, [dailyRows, rateMap]);

  const stats = useMemo(() => {
    const total = workerRows.length;
    const avgScore = total > 0 ? Math.round(workerRows.reduce((s, r) => s + r.score, 0) / total) : 0;
    const totalPres = workerRows.reduce((s, r) => s + r.presMin, 0);
    const totalDays = workerRows.reduce((s, r) => s + r.days, 0);
    const top = workerRows[0];
    return { total, avgScore, totalPres, totalDays, top };
  }, [workerRows]);

  const toggleRow = (id) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  // ─── exports ───────────────────────────────────────────────────────────────
  const TABLE_HEADERS = ['Renditja', 'Punëtori', 'Pozita', 'Departamenti', 'Ditë', 'Mes. Prezence', 'Mes. TT', 'Prezenca %', 'Realizimi %', 'Në kohë %', 'Skor', 'Vlerësimi'];
  const exportRows = () => workerRows.map((r, i) => [
    i + 1, r.name || '—', wPosition(r.workerId) || '—', deptName(wDeptId(r.workerId)) || '—',
    r.days, fmtHHMM(r.avgPres), fmtHHMM(r.avgTT),
    `${r.presScore}%`, `${r.realScore}%`, `${r.onTimePct}%`,
    `${r.score}`, scoreBand(r.score).label,
  ]);

  const exportCSV = () => {
    const lines = [TABLE_HEADERS, ...exportRows()].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(','));
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(lines.join('\n'));
    a.download = 'vleresimi-' + todayStr() + '.csv'; a.click();
  };

  const exportExcel = () => {
    const ws = XLSX.utils.aoa_to_sheet([TABLE_HEADERS, ...exportRows()]);
    ws['!cols'] = [8, 24, 18, 18, 6, 12, 10, 10, 12, 10, 8, 14].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vlerësimi');
    XLSX.writeFile(wb, 'vleresimi-' + todayStr() + '.xlsx');
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
    doc.text('Vlerësimi i Punëtorit', 14, 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.text(`Periudha: ${dateFrom} — ${dateTo}`, 14, 22);
    doc.text('Gjeneruar: ' + todayStr(), 280, 22, { align: 'right' });
    autoTable(doc, {
      head: [TABLE_HEADERS], body: exportRows(), startY: 27,
      styles: { fontSize: 7.5, cellPadding: 1.8 },
      headStyles: { fillColor: [62, 143, 163], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 248, 250] },
    });
    const pg = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pg; i++) {
      doc.setPage(i); doc.setFontSize(7); doc.setTextColor(150);
      doc.text('Faqe ' + i + '/' + pg, doc.internal.pageSize.width - 14, doc.internal.pageSize.height - 8, { align: 'right' });
    }
    doc.save('vleresimi-' + todayStr() + '.pdf');
  };

  const resetFilters = () => { setFWorker(''); setFDept(''); setFSearch(''); };
  const hasFilters   = fWorker || fDept || fSearch;

  // ─── render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ px: { xs: 0, sm: 0 }, py: { xs: 1, sm: 1.5 }, width: '100%', minWidth: 0 }}>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.75} alignItems="center" sx={{ mb: 2.5 }}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: 2.5, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'primary.soft', color: 'primary.main',
          }}
        >
          <LeaderboardIcon />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.35rem', md: '1.5rem' },
              letterSpacing: '-0.01em',
              color: 'text.primary',
              lineHeight: 1.2,
            }}
          >
            Performanca e punëtorëve
          </Typography>
          <Typography sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.875rem', lineHeight: 1.5 }}>
            Monitoroni produktivitetin, prezencën dhe trendet e performancës së punëtorëve.
          </Typography>
        </Box>
      </Stack>

      {/* ── Filter toolbar (compact, 44px controls) ─────────────────────── */}
      <Paper
        elevation={0}
        sx={{ p: 1.5, mb: 2.5, borderRadius: 2, border: 1, borderColor: 'divider' }}
      >
        <Grid container spacing={1.25} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="Kërko emër, pozitë, departament…"
              value={fSearch}
              onChange={e => setFSearch(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: fSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFSearch('')}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 38, bgcolor: 'background.paper', fontSize: '0.875rem' },
                '& .MuiOutlinedInput-input': { py: 0 },
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              type="date" fullWidth size="small" label="Nga"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 38 }, '& .MuiOutlinedInput-input': { py: 0 } }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              type="date" fullWidth size="small" label="Deri"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 38 }, '& .MuiOutlinedInput-input': { py: 0 } }}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <TextField
              select fullWidth size="small" label="Departamenti"
              value={fDept}
              onChange={e => { setFDept(e.target.value); setFWorker(''); }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 38 }, '& .MuiOutlinedInput-input': { py: 0, display: 'flex', alignItems: 'center' } }}
            >
              <MenuItem value=""><em>Të gjitha</em></MenuItem>
              {departments.map(d => <MenuItem key={d.id || d.Id} value={d.id || d.Id}>{d.name || d.Name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <TextField
              select fullWidth size="small" label="Punëtori"
              value={fWorker}
              onChange={e => setFWorker(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 38 }, '& .MuiOutlinedInput-input': { py: 0, display: 'flex', alignItems: 'center' } }}
            >
              <MenuItem value=""><em>Të gjithë</em></MenuItem>
              {deptWorkers.map(w => (
                <MenuItem key={w.Id || w.id} value={w.Id || w.id}>
                  {w.FirstName || w.first_name} {w.LastName || w.last_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              {hasFilters && (
                <Button
                  variant="text"
                  onClick={resetFilters}
                  sx={{ height: 38, color: 'text.secondary', px: 1.5 }}
                >
                  Pastro
                </Button>
              )}
              <Tooltip title="Rifresko">
                <IconButton
                  onClick={load}
                  sx={{
                    width: 38, height: 38, borderRadius: 1.5,
                    border: 1, borderColor: 'divider',
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* ── KPI cards (app-wide idiom: icon tile + uppercase label) ─────── */}
      <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: 2.5 }}>
        {[
          { key: 'employees',    icon: GroupsIcon,      label: 'Punëtorë të gjurmuar', val: stats.total,              sub: `${stats.totalDays} ditë gjithsej`,                                           tone: 'primary' },
          { key: 'attendance',   icon: AccessTimeIcon,  label: 'Prezenca totale',      val: fmtHHMM(stats.totalPres), sub: `${fmtHHMM(stats.totalPres / Math.max(1, stats.totalDays))} mesatarja/ditë`, tone: 'info'    },
          { key: 'avgScore',     icon: SpeedIcon,       label: 'Mesatarja e skorit',   val: stats.avgScore + '%',     sub: scoreBand(stats.avgScore).label,                                               tone: stats.avgScore >= 75 ? 'success' : 'warning' },
          { key: 'topPerformer', icon: EmojiEventsIcon, label: 'Më i miri',            val: stats.top ? stats.top.name.split(' ')[0] : '—', sub: stats.top ? `${stats.top.score}% performancë` : 'Pa të dhëna', tone: 'warning' },
        ].map(c => {
          const Icon = c.icon;
          return (
            <Grid item xs={6} md={3} key={c.key}>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  border: 1, borderColor: 'divider',
                  borderRadius: 2.5,
                  p: 2,
                  height: '100%',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  transition: 'border-color 0.15s ease',
                  '&:hover': { borderColor: 'grey.300' },
                }}
              >
                <Box
                  sx={{
                    width: 38, height: 38, borderRadius: 2, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: `${c.tone}.soft`, color: `${c.tone}.main`,
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.3 }}>
                    {c.label}
                  </Typography>
                  {loading ? (
                    <LinearProgress sx={{ borderRadius: 999, mt: 1, mb: 0.75 }} />
                  ) : (
                    <>
                      <Typography
                        noWrap
                        sx={{
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                          fontWeight: 800, color: 'text.primary',
                          lineHeight: 1.2, letterSpacing: '-0.01em',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {c.val}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }} noWrap>
                        {c.sub}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* ── Leaderboard table ───────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {/* Table header bar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 1.5 }}
        >
          <Box>
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Renditja e punëtorëve
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.25 }}>
              {workerRows.length} {workerRows.length === 1 ? 'punëtor' : 'punëtorë'} në këtë periudhë
            </Typography>
          </Box>

          {/* Export menu (single button → dropdown) */}
          <Box>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: '15px !important' }} />}
              onClick={(e) => setExportAnchor(e.currentTarget)}
              sx={{ height: 38, borderRadius: 1.5, px: 1.75, fontSize: '0.8125rem' }}
            >
              Eksporto
            </Button>
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { exportCSV(); setExportAnchor(null); }}>
                <ListItemIcon><DownloadIcon sx={{ fontSize: 16 }} /></ListItemIcon>
                <ListItemText primary="CSV" secondary=".csv tekst i ndarë me presje" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '0.75rem' }} />
              </MenuItem>
              <MenuItem onClick={() => { exportExcel(); setExportAnchor(null); }}>
                <ListItemIcon><DownloadIcon sx={{ fontSize: 16 }} /></ListItemIcon>
                <ListItemText primary="Excel" secondary=".xlsx për Microsoft Excel" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '0.75rem' }} />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { exportPDF(); setExportAnchor(null); }}>
                <ListItemIcon><DownloadIcon sx={{ fontSize: 16 }} /></ListItemIcon>
                <ListItemText primary="PDF" secondary="Raport i printueshëm" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '0.75rem' }} />
              </MenuItem>
            </Menu>
          </Box>
        </Stack>

        {/* Table */}
        <Box sx={{ overflowX: 'auto', maxHeight: 720, overflowY: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  { label: '#',             align: 'left',   width: 64 },
                  { label: 'Punëtori',      align: 'left' },
                  { label: 'Ditë',          align: 'center' },
                  { label: 'Mes. prezencë', align: 'center' },
                  { label: 'Prezenca',      align: 'left' },
                  { label: 'Realizimi',     align: 'left' },
                  { label: 'Në kohë',       align: 'left' },
                  { label: 'Skor',          align: 'right' },
                ].map((h) => (
                  <TableCell
                    key={h.label}
                    align={h.align}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.6875rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                      py: 1.5,
                      bgcolor: 'background.paper',
                      borderTop: 1,
                      borderBottom: 1,
                      borderColor: 'divider',
                      whiteSpace: 'nowrap',
                      ...(h.width ? { width: h.width } : {}),
                    }}
                  >
                    {h.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, borderBottom: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : workerRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center', borderBottom: 0 }}>
                    <Box
                      sx={{
                        width: 48, height: 48, borderRadius: 2,
                        bgcolor: 'grey.100',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        mb: 1.5,
                      }}
                    >
                      <EmojiEventsIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                      Nuk ka të dhëna
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Ndrysho filtrat ose periudhën për të parë rezultate
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : workerRows.map((r, i) => {
                const band   = scoreBand(r.score);
                const isOpen = expanded.has(r.workerId);
                const rank   = i + 1;
                // Top-3 ranking pills use semantic tints; rest are neutral.
                const rankStyle = rank === 1
                  ? { bg: 'warning.soft', fg: 'warning.dark' }
                  : rank === 2
                    ? { bg: 'grey.100',   fg: 'text.primary' }
                    : rank === 3
                      ? { bg: 'warning.soft', fg: 'warning.dark', alpha: 0.6 }
                      : { bg: 'grey.50',    fg: 'text.secondary' };

                return (
                  <React.Fragment key={r.workerId}>
                    <TableRow
                      hover
                      onClick={() => toggleRow(r.workerId)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '& td': { borderBottom: isOpen ? 0 : undefined },
                        '&:hover': { bgcolor: 'grey.50' },
                      }}
                    >
                      {/* Rank */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <IconButton size="small" sx={{ width: 24, height: 24 }}>
                            {isOpen
                              ? <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                              : <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                          <Box
                            sx={{
                              minWidth: 28, height: 22, px: 0.75, borderRadius: 999,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: rankStyle.bg,
                              color: rankStyle.fg,
                              fontSize: '0.75rem', fontWeight: 700,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            #{rank}
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Worker */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={wAvatar(r.workerId) || undefined}
                            sx={{
                              width: 36, height: 36,
                              fontSize: '0.8125rem', fontWeight: 600,
                              bgcolor: 'primary.soft',
                              color: 'primary.main',
                            }}
                          >
                            {!wAvatar(r.workerId) && initials(r.name)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.005em' }} noWrap>
                              {r.name || '—'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500, mt: 0.25 }} noWrap>
                              {wPosition(r.workerId) || '—'}{deptName(wDeptId(r.workerId)) ? ` · ${deptName(wDeptId(r.workerId))}` : ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Days */}
                      <TableCell align="center" sx={{ py: 1.75 }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                          {r.days}
                        </Typography>
                      </TableCell>

                      {/* Avg presence */}
                      <TableCell align="center" sx={{ py: 1.75 }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtHHMM(r.avgPres)}
                        </Typography>
                      </TableCell>

                      {/* Presence % */}
                      <TableCell sx={{ py: 1.75, minWidth: 110 }}>
                        <ProgressCell value={r.presScore} />
                      </TableCell>

                      {/* Realization % */}
                      <TableCell sx={{ py: 1.75, minWidth: 110 }}>
                        <ProgressCell value={r.realScore} />
                      </TableCell>

                      {/* On-time % */}
                      <TableCell sx={{ py: 1.75, minWidth: 110 }}>
                        <ProgressCell value={r.onTimePct} />
                      </TableCell>

                      {/* Score + band */}
                      <TableCell align="right" sx={{ py: 1.75 }}>
                        <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
                          <Typography
                            sx={{
                              fontSize: '1.0625rem', fontWeight: 700, minWidth: 32,
                              color: 'text.primary', fontVariantNumeric: 'tabular-nums',
                              letterSpacing: '-0.01em',
                            }}
                          >
                            {r.score}
                          </Typography>
                          <Chip size="small" color={band.color} label={band.label} sx={{ height: 22, fontWeight: 600, fontSize: '0.6875rem' }} />
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Expanded daily breakdown */}
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, borderBottom: isOpen ? undefined : 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2.5, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
                            <Typography
                              variant="overline"
                              sx={{ color: 'text.disabled', display: 'block', mb: 1.25, lineHeight: 1.4 }}
                            >
                              Detajet ditore · {r.firstDay} → {r.lastDay}
                            </Typography>
                            <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    {['Data', 'Hyrja', 'Dalja', 'Pauza (min)', 'Prezenca', 'TT', 'Realizimi'].map(h => (
                                      <TableCell
                                        key={h}
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: '0.6875rem',
                                          letterSpacing: '0.04em',
                                          textTransform: 'uppercase',
                                          color: 'text.disabled',
                                          py: 1, bgcolor: 'background.paper',
                                          borderBottom: 1, borderColor: 'divider',
                                        }}
                                      >
                                        {h}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {r.items.map(d => (
                                    <TableRow key={d.day}>
                                      <TableCell sx={{ fontSize: '0.8125rem', fontFamily: 'monospace', py: 1.25 }}>{d.day}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8125rem', fontFamily: 'monospace', py: 1.25, color: d.checkIn ? 'success.dark' : 'text.disabled' }}>{fmtTime(d.checkIn)}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8125rem', fontFamily: 'monospace', py: 1.25, color: d.checkOut ? 'error.dark' : 'text.disabled' }}>{fmtTime(d.checkOut)}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8125rem', py: 1.25 }}>{d.breakMin > 0 ? d.breakMin : '—'}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8125rem', fontFamily: 'monospace', py: 1.25, fontWeight: 600 }}>{fmtHHMM(d.presMin)}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8125rem', fontFamily: 'monospace', py: 1.25, fontWeight: 600 }}>{d.ttMin > 0 ? fmtHHMM(d.ttMin) : '0:00'}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8125rem', py: 1.25, fontWeight: 600, color: d.realizimPct >= 75 ? 'success.dark' : d.realizimPct >= 50 ? 'warning.dark' : 'error.dark' }}>
                                        {d.realizimPct}%
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Subcomponent: progress bar with label ──────────────────────────────────
function ProgressCell({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const tone = v >= 90 ? 'success' : v >= 75 ? 'primary' : v >= 50 ? 'warning' : 'error';
  return (
    <Stack spacing={0.75} sx={{ minWidth: 90 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
          {v}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={v}
        color={tone}
        sx={{ height: 6 }}
      />
    </Stack>
  );
}
